<template>
  <div class="control-panel-container">
    <ContentContainerNavbar title="Control Panel" button buttonText="Update Tablets" buttonEvent="update-all-devices" @update-all-devices="updateTablets" />
    <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
    <v-table v-else class="table" fixed-header :hover="deviceList.length !== 0">
      <thead>
        <tr>
          <th class="text-left font-weight-black cursor-pointer checkbox-cell">
            <v-icon class="cursor-pointer pr-6" :icon="filterActive ? 'mdi-filter-remove' : 'mdi-filter-menu'" @click="filterActive = !filterActive"></v-icon>
            {{ $t('i18n.uniqueIdentifier') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.name') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.type') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.completionRedirectUrl') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.ipAddress') }}
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.shortURL') }}
          </th>
          <th class="text-left font-weight-black">
            App version
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.associatedForm') }}
          </th>
          <th class="text-left font-weight-black">
            Last Heartbeat
          </th>
          <th class="text-left font-weight-black">
            {{ $t('i18n.actions') }}
          </th>
        </tr>
        <tr v-if="filterActive">
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.uuid" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.name" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.type" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.completion_redirect_url" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.ip_address" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.short_url" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.app_version" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.last_heartbeat" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black">
            <v-text-field v-model="filters.form" density="compact" variant="outlined" :hide-details="true" clearable></v-text-field>
          </th>
          <th class="text-left font-weight-black"></th>
        </tr>
      </thead>
      <tbody v-if="deviceList.length !== 0">
        <tr v-for="(device, index) in filteredDevices()" :key="index" class="text-left">
          <td>{{ device.uuid }}</td>
          <td>{{ device.name }}</td>
          <td>{{ deviceTypeName(device.type_id) }}</td>
          <td>{{ device.completion_redirect_url }}</td>
          <td>{{ device.ip_address }}</td>
          <td>{{ device.short_url }}</td>
          <td>{{ device.app_version }}</td>
          <td>{{ device.form_id !== null ? formName(device.form_id) : null }}</td>
          <td>{{ formatHeartbeat(device.last_heartbeat) }}</td>
          <td class="text-left px-0">
            <!-- Qr Code download -->
            <v-btn v-if="deviceTypeName(device.type_id) === 'QRCODE'" @click="downloadQRCode(device)" @download-complete="resetQRModal()" class="cursor-pointer" variant="text" size="small" icon>
              <v-icon icon="mdi-qrcode-scan" size="large"></v-icon>
              <v-tooltip activator="parent" location="start">
                {{ $t('i18n.downloadQRCode') }}
              </v-tooltip>
            </v-btn>
            <!-- ou Refresh content -->
            <v-btn v-else class="cursor-pointer" variant="text" size="small" icon @click="refreshDeviceContent(index)">
              <v-icon icon="mdi-play"></v-icon>
              <v-tooltip activator="parent" location="start">
                {{ $t('i18n.btnRefreshContent') }}
              </v-tooltip>
            </v-btn>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr class="no-hover">
          <td colspan="9" class="text-center"> {{ $t('i18n.noDataAvailable') }}</td>
        </tr>
      </tbody>
    </v-table>
  </div>
  <!--
   Modals are below
  -->
  <QrCodeModal v-if="qrCodeModalVisible && selectedQRCode" :tinyUrl="selectedQRCode.short_url" :label="selectedQRCode.name" logoName="icon-only.png" @download-complete="resetQRModal()" @qrcode-error="appStore.addAlert('error', 'Error creating QR Code.')" />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import ContentContainerNavbar from '../navbars/ContentContainerNavbar.vue'
import { Device, DeviceType, Form } from '@/utils/DataTypes'
import { api, findDeviceTypeById, formatHeartbeat } from '@/utils/HelperFunctions'
import QrCodeModal from '../modals/QrCodeModal.vue'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'ControlPanel',
  setup() {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore,
      formatHeartbeat
    }
  },
  components: {
    ContentContainerNavbar,
    QrCodeModal
  },
  async mounted() {
    this.deviceTypeList = this.appStore.getAppState.deviceTypeList
    await this.getFormList()
    await this.getDeviceList()
    this.loading = false
  },
  data() {
    return {
      formList: [] as Form[],

      filterActive: false,
      loading: true,
      qrCodeModalVisible: false,

      deviceTypeList: [] as DeviceType[],
      deviceList: [] as Device[],

      filters: {
        uuid: '',
        name: '',
        type: '',
        completion_redirect_url: '',
        ip_address: '',
        short_url: '',
        app_version: '',
        last_heartbeat: '',
        form: ''
      },

      selectedQRCode: null as Device | null
    }
  },
  methods: {
    async updateTablets() {
      try {
        const url = '/control-panel/order-tablets-upgrade'
        await api(url, 'post')
        this.appStore.addAlert('info', 'Device firmware upgrade requested.')
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t request device firmware upgrade.')
      }
    },
    async getFormList() {
      try {
        const url = '/forms-simplified'
        const getFormListResponse = await api(url, 'get')

        if (!getFormListResponse) return

        this.formList = getFormListResponse.data
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get form list.')
      }
    },
    async getDeviceList() {
      try {
        const url = '/devices'
        const getDeviceListResponse = await api(url, 'get')

        if (!getDeviceListResponse) return

        this.deviceList = getDeviceListResponse.data
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get device list.')
      }
    },
    refreshDeviceContent(index: number) {
      const url = '/devices/refresh-content'
      api(url, 'post')
        .then(() => {
          this.appStore.addAlert('info', 'Content refresh requested.')
        })
        .catch(() => {
          this.appStore.addAlert('error', 'Couldn\'t request content refresh.')
        })
    },
    navigate(routeName: string) {
      this.router.push({ name: routeName })
    },
    formName(formID: number | undefined) {
      const form = this.formList.find(form => form.id === formID)
      return form ? form.name : null
    },
    deviceTypeName(typeID: number | null) {
      if (typeID === null) {
        return 'NULL'
      }
      const itemTypeName = findDeviceTypeById(this.deviceTypeList, typeID)
      if (itemTypeName === null) {
        return 'N/A'
      } else {
        return itemTypeName
      }
    },
    filteredDevices() {
      let filteredDevices = this.deviceList as Device[]

      if (this.filters.uuid) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.uuid.toLowerCase().includes(this.filters.uuid.toLowerCase())
        )
      }
      if (this.filters.name) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.name.toLowerCase().includes(this.filters.name.toLowerCase())
        )
      }
      if (this.filters.type) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          this.deviceTypeName(device.type_id).toLowerCase().includes(this.filters.type.toLowerCase())
        )
      }
      if (this.filters.completion_redirect_url) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.completion_redirect_url?.toLowerCase().includes(this.filters.completion_redirect_url.toLowerCase())
        )
      }
      if (this.filters.ip_address) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.ip_address?.toLowerCase().includes(this.filters.ip_address.toLowerCase())
        )
      }
      if (this.filters.short_url) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.short_url?.toLowerCase().includes(this.filters.short_url.toLowerCase())
        )
      }
      if (this.filters.app_version) {
        filteredDevices = filteredDevices.filter((device) => {
          if (device.app_version !== undefined && device.app_version) {
            return device.app_version.includes(this.filters.app_version.toLowerCase())
          }
          return null
        })
      }
      if (this.filters.last_heartbeat) {
        filteredDevices = filteredDevices.filter((device) => {
          if (device.last_heartbeat !== undefined && device.last_heartbeat) {
            return device.last_heartbeat.includes(this.filters.last_heartbeat.toLowerCase())
          }
          return null
        })
      }
      if (this.filters.form) {
        filteredDevices = filteredDevices.filter((device) => {
          if (device.form_id !== undefined && device.form_id) {
            return this.formName(device.form_id)?.toLowerCase().includes(this.filters.form.toLowerCase())
          }
          return null
        })
      }
      return filteredDevices
    },
    toggleFilter() {
      this.filterActive = !this.filterActive
      this.filters = {
        uuid: '',
        name: '',
        type: '',
        ip_address: '',
        completion_redirect_url: '',
        short_url: '',
        app_version: '',
        last_heartbeat: '',
        form: ''
      }
    },
    resetQRModal() {
      this.selectedQRCode = null
      this.qrCodeModalVisible = false
    },
    downloadQRCode(selectedQRCode: Device) {
      this.selectedQRCode = selectedQRCode
      this.qrCodeModalVisible = true
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.control-panel-container {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  box-shadow: 2px 0 15px 0 rgba(0, 0, 0, 0.08);

  .table {
    margin: 1.0em;
    padding: 1.0em 2.0em;
    border-radius: 15px;
    overflow-y: auto;

    .checkbox-cell {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: left;
      align-items: center;
    }
  }
}
</style>
